import { Button, Card, errorMessage, toast } from '@bixo/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { ShieldQuestion } from 'lucide-react'
import { qk } from '../../lib/api'
import { fetchGst, gstKey, saveGst } from './gst-api'

/**
 * Onboarding: "Are you registered under GST?", asked once on the dashboard
 * until the business answers. Yes leads to adding the GSTIN; No turns GST off.
 */
export function GstQuestionCard() {
  const queryClient = useQueryClient()
  const gst = useQuery({ queryKey: gstKey, queryFn: fetchGst })
  const answerNo = useMutation({
    mutationFn: () => saveGst({ enabled: false }),
    onSuccess: async (next) => {
      queryClient.setQueryData(gstKey, next)
      await queryClient.invalidateQueries({ queryKey: qk.me })
      toast.success('Done: bills will be plain invoices without GST. You can change this in Settings.')
    },
    onError: (e) => toast.error(errorMessage(e)),
  })

  if (!gst.data || gst.data.decided) return null

  return (
    <Card className="flex flex-col items-start gap-3 p-5 sm:flex-row sm:items-center">
      <ShieldQuestion className="size-5 text-primary" />
      <div className="flex-1">
        <p className="text-sm font-medium">Is your business registered under GST?</p>
        <p className="text-xs text-muted-foreground">
          GST-registered businesses issue tax invoices and file GST returns. Others issue plain bills.
        </p>
      </div>
      <div className="flex gap-2">
        <Button size="sm" variant="ghost" loading={answerNo.isPending} onClick={() => answerNo.mutate()}>
          No
        </Button>
        <Button asChild size="sm" variant="secondary">
          <Link to="/settings/business">Yes, add GSTIN</Link>
        </Button>
      </div>
    </Card>
  )
}
